import { JobseekerFilter } from 'context/JobseekerFilterContext';
import { Jobseeker } from 'graphql/generated';
import _ from 'lodash';
import { getSearchParams } from 'utils/browser-utils';
import { isEmpty } from 'utils/validationHelper';

export const extractJobseekerFilterFromQueryString = (): JobseekerFilter => {
    const { salaryMin, salaryMax, page, orderBy, keySkills, ...searchParams } = getSearchParams();

    const filter: JobseekerFilter = searchParams;

    if (keySkills) {
        const skills = (keySkills as string).split(',');
        filter.keySkills = skills;
    }

    if (!isEmpty(salaryMin) || !isEmpty(salaryMax)) {
        filter.salary = {
            from: isEmpty(salaryMin) ? 0 : Number(salaryMin),
            to: isEmpty(salaryMax) ? 0 : Number(salaryMax),
        };
    }
    return filter;
};

export const getFilteredRequirements = (requirements: string[] = [], jobseekerProfile) => {
    const requirementMap = {
        FIRSTNAME: 'firstName',
        LASTNAME: 'lastName',
        SALARY_EXPECTATION: 'salaryExpectation',
        SUMMARY: 'summary',
        EMAILS: 'contacts.emails',
        PHONES: 'contacts.phones',
        LINKEDIN_URL: 'contacts.linkedInUrl',
        EDUCATIONS: 'educations',
    };

    const requiredFields = requirements.map((r) => requirementMap[r]);

    const jobseeker = jobseekerProfile?.me?.jobseeker;

    const filteredRequirements = requiredFields?.filter((requiredField) => {
        const requirementFieldsValue = _.get(jobseeker, requiredField);

        if (!requirementFieldsValue) {
            return true;
        }
        if (Array.isArray(requirementFieldsValue) && requirementFieldsValue.length === 0) {
            return true;
        }
        if (requirementFieldsValue.edges && requirementFieldsValue.edges.length === 0) {
            return true;
        }
        return false;
    });
    return filteredRequirements;
};

export type ResumeSectionRefs = {
    profileInfo: React.MutableRefObject<null>;
    emails: React.MutableRefObject<null>;
    phones: React.MutableRefObject<null>;
    skills: React.MutableRefObject<null>;
    socials: React.MutableRefObject<null>;
    experiences: React.MutableRefObject<null>;
    educations: React.MutableRefObject<null>;
    languages: React.MutableRefObject<null>;
};

export type UnfilledSection = {
    fields: string[];
    buttonRef: React.MutableRefObject<null>;
    sectionName: string;
    descriptionHeader: string;
    description: string;
    buttonName: string;
    icon: string;
};

export const getUnfilledProfileSections = (
    sections: string[] = [],
    sectionRefs: ResumeSectionRefs,
    jobseekerProfile: Jobseeker,
): UnfilledSection[] => {
    const sectionsMap = {
        PERSONALINFORMATIONS: {
            fields: [
                'firstName',
                'lastName',
                'middleName',
                'dateOfBirth',
                'placeOfBirth',
                'region',
                'gender',
                'status',
                'salaryExpectation',
                'summary',
            ],
            buttonRef: sectionRefs.profileInfo,
            sectionName: sections[0],
            descriptionHeader: 'descriptionHeaderPersonalInfo',
            description: 'descriptionPersonalInfo',
            buttonName: 'buttonPersonalInfo',
            icon: 'profile-info.svg',
        },
        EMAILS: {
            fields: ['contacts.emails'],
            buttonRef: sectionRefs.emails,
            sectionName: sections[1],
            descriptionHeader: 'descriptionHeaderEmails',
            description: 'descriptionEmails',
            buttonName: 'buttonEmails',
            icon: 'emails.svg',
        },
        PHONES: {
            fields: ['contacts.phones'],
            buttonRef: sectionRefs.phones,
            sectionName: sections[2],
            descriptionHeader: 'descriptionHeaderPhones',
            description: 'descriptionPhones',
            buttonName: 'buttonPhones',
            icon: 'phones.svg',
        },
        SOCIALMEDIAACCOUNTS: {
            fields: [
                'contacts.linkedInUrl',
                'contacts.githubUrl',
                'contacts.twitterUrl',
                'contacts.skypeAddress',
                'contacts.facebookUrl',
                'contacts.instagramUrl',
            ],
            buttonRef: sectionRefs.socials,
            sectionName: sections[3],
            descriptionHeader: 'descriptionHeaderSocialMediaAccounts',
            description: 'descriptionSocialMediaAccounts',
            buttonName: 'buttonSocialMediaAccounts',
            icon: 'socials.svg',
        },
        SKILLS: {
            fields: ['keySkills'],
            buttonRef: sectionRefs.skills,
            sectionName: sections[4],
            descriptionHeader: 'descriptionHeaderSkills',
            description: 'descriptionSkills',
            buttonName: 'buttonSkills',
            icon: 'skills.svg',
        },
        EXPERIENCES: {
            fields: ['experiences'],
            buttonRef: sectionRefs.experiences,
            sectionName: sections[5],
            descriptionHeader: 'descriptionHeaderExperiences',
            description: 'descriptionExperiences',
            buttonName: 'buttonExperiences',
            icon: 'experiences.svg',
        },
        EDUCATIONS: {
            fields: ['educations'],
            buttonRef: sectionRefs.educations,
            sectionName: sections[6],
            descriptionHeader: 'descriptionHeaderEducations',
            description: 'descriptionEducations',
            buttonName: 'buttonEducations',
            icon: 'educations.svg',
        },
        LANGUAGES: {
            fields: ['languages'],
            buttonRef: sectionRefs.languages,
            sectionName: sections[7],
            descriptionHeader: 'descriptionHeaderLanguages',
            description: 'descriptionLanguages',
            buttonName: 'buttonLanguages',
            icon: 'languages.svg',
        },
    };
    const sectionsFields = sections.map((el) => sectionsMap[el]);

    const unfilledSections = sectionsFields.filter((sectionField) => {
        const isSectionFilled = sectionField.fields.some((field) => {
            const sectionFieldsValue = _.get(jobseekerProfile, field);
            if (!sectionFieldsValue) {
                return false;
            }
            if (Array.isArray(sectionFieldsValue) && sectionFieldsValue.length === 0) {
                return false;
            }
            if (sectionFieldsValue.edges && sectionFieldsValue.edges.length === 0) {
                return false;
            }
            return true;
        });
        return !isSectionFilled;
    });
    return unfilledSections;
};
